/**
 * Geolocation Class
 *
 * @version 1.0.0
 * @copyright 2019 SEDA.digital GmbH & Co. KG
 */

// NOTE: When importing modules from npm, make sure to check if they use ES6 syntax or not.
// If so, add them to the include regex for babel-loader in webpack.config.js
import ClassLogger from 'ClassLogger';
// to import legacy 3rd party scripts (into the global space) use the script-loader like this:
// import legacyScript from 'script-loader!../../vendor/legacy/script.js';

class Geolocation {
    getClassName() {
        return 'Geolocation';
    }

    constructor(options) {
        let self = this;
        self.logger = ClassLogger(self, true); // set second parameter to false to disable logging
        self.last_xhr = null;

        // init properties
        self.defaults = {
            geocoding_api_version: 'v2',
            apiurl: '',
        };
        self.options = Object.assign(self.defaults, options);

        document.querySelectorAll('[data-autofill-source]').forEach(function (element) {
            let source = element.getAttribute('data-autofill-source');
            if (!source || source === '' || source === null) {
                return;
            }
            let sourceElem = document.querySelector('input[name="' + source + '"]');
            if (sourceElem.length === 0) {
                return;
            }

            self.addGeocodingListener(sourceElem, element);
        });
    }

    addGeocodingListener(zipCodeElement, citiesElement) {
        let self = this;
        //self.logger.log('addGeocodingListener is running...');
        zipCodeElement.setAttribute('data-lastvalue', '');
        zipCodeElement.addEventListener('change', function (event) {
            //self.logger.debug('zip change', event);
            self.geocodeZipcode(event, zipCodeElement, citiesElement);
        });
        zipCodeElement.addEventListener('keyup', function (event) {
            //self.logger.debug('zip keyup');
            self.geocodeZipcode(event, zipCodeElement, citiesElement);
        });
        document.querySelector('select[name="country"]').addEventListener('change', function (event) {
            //self.logger.debug('country change');
            self.geocodeZipcode(event, zipCodeElement, citiesElement);
        });

        citiesElement.addEventListener('change', function (event) {
            //self.logger.debug('cities change');
            let option = citiesElement.querySelector('option[data-zipcode]:checked');
            if (option !== null && zipCodeElement.value !== option.getAttribute('data-zipcode')) {
                zipCodeElement.value = option.getAttribute('data-zipcode');
            }
        });
    }

    geocodeZipcode(event, zipCodeElement, citiesElement) {
        let self = this;
        // abort previous xhr request
        // TODO: rewrite with AbortController
        // https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort
        //if (self.last_xhr !== null && typeof self.last_xhr.abort === 'function') self.last_xhr.abort();

        let country = 'DE';
        let selectElem = document.querySelector('select[name="country"]');
        if (selectElem.length) {
            country = selectElem[selectElem.selectedIndex].value.toUpperCase();
        }
        let supportedCountries = ['DE', 'AT', 'CH'];
        if (supportedCountries.indexOf(country) === -1) {
            this.addAndShowAltCityField(citiesElement);
            return;
        }
        //this.addAndShowCitySelector(citiesElement);

        let value = String(zipCodeElement.value);
        if (value.length < 3) {
            // no enough data for zip code, no need to fire request
            return;
        }

        if (value + country === zipCodeElement.getAttribute('data-lastvalue')) {
            // same value as before, no need to fire request
            return;
        }
        zipCodeElement.setAttribute('data-lastvalue', value + country);

        self.geocodingRequest(value, country).then(function (locations) {
            self.last_xhr = null;

            //self.logger.log(locations.data);
            // Remove all previous records/options
            citiesElement.querySelectorAll('option').forEach(function (option) {
                option.remove();
            });

            if (locations.data.length === 0) {
                //self.logger.debug('No locations found for given zip code, allowing manual input');
                self.addAndShowAltCityField(citiesElement);
                return;
            }
            self.addAndShowCitySelector(citiesElement);

            let communitiesAlreadyPresent = [];
            let communities = {};
            locations.data.forEach(function (location) {
                communities[location.community.name.trim()] = location;
                if (location.city.name === location.community.name) {
                    // adding (xx as a sorting hack [later removed]
                    location.city.name += ' (xx';
                    communitiesAlreadyPresent.push(location.community.name);
                }
            });

            for (let communityName in communities) {
                if (communitiesAlreadyPresent.includes(communityName) === false) {
                    let communityItem = Object.assign({}, communities[communityName]);
                    communityItem.city = {
                        id: null,
                        // adding (xx as a sorting hack [later removed]
                        name: communityItem.community.name + ' (xx',
                        zipcode: communityItem.city.zipcode,
                        coordinates: null
                    }
                    communityItem.location_id = '';
                    locations.data.push(communityItem);
                }
            }

            if (locations.data.length === 1) {
                //self.logger.debug('Single location found for given zip code');
                citiesElement.insertAdjacentHTML('beforeend', '<option data-zipcode="' + locations.data[0].city.zipcode + '" data-locationid="' + locations.data[0].location_id + '" selected>' + locations.data[0].city.name.replace(' (xx', '') + '</option>');
                citiesElement.value = locations.data[0].city.name.replace(' (xx', '');
                return;
            }

            // Sort Items
            locations.data.sort(function (a, b) {
                let aName = a.city.name.toUpperCase();
                let bName = b.city.name.toUpperCase();
                if (aName < bName) {
                    return -1;
                }
                if (aName > bName) {
                    return 1;
                }
                return 0;
            });


            //self.logger.debug('Multiple locations found for given zip code');
            // TRANSLATIONS ??
            citiesElement.insertAdjacentHTML('beforeend', '<option value="" disabled selected>Bitte auswählen</option>');
            citiesElement.value = '';

            locations.data.forEach(function (location) {
                citiesElement.insertAdjacentHTML('beforeend', '<option data-zipcode="' + location.city.zipcode + '" data-locationid="' + location.location_id + '">' + location.city.name.replace(' (xx', '') + '</option>');
            });

            if (citiesElement.getAttribute('data-defaultvalue') !== '') {
                // select most accurate option, if any
                let defaultElement = citiesElement.querySelector('option:contains(' + citiesElement.getAttribute('data-defaultvalue') + ')');
                if (defaultElement !== null) {
                    defaultElement.setAttribute('selected', 'selected');
                    citiesElement.value = citiesElement.getAttribute('data-defaultvalue');
                }
            }
        });
    }

    addAndShowAltCityField(citiesElement) {
        //this.logger.debug('Displaying manual city field');
        citiesElement.value = '';
        citiesElement.setAttribute('disabled', 'disabled');
        citiesElement.style.display = 'none';
        if (!citiesElement.parentElement.querySelector('.alt-city-input')) {
            //this.logger.debug('Field not found, creating');
            citiesElement.insertAdjacentHTML('afterend', '<input type="text" name="city" class="alt-city-input">');
        }
    }

    addAndShowCitySelector(citiesElement) {
        //this.logger.debug('Showing city selector');
        citiesElement.removeAttribute('disabled');
        citiesElement.style.display = 'block';
        if (citiesElement.parentElement.querySelector('.alt-city-input')) {
            //this.logger.debug('Manual city field found, removing');
            citiesElement.parentElement.querySelector('.alt-city-input').remove();
        }
    }

    geocodingRequest(zipcode, country) {
        let self = this;
        if (typeof country === 'undefined') {
            country = 'DE';
        }

        let xhr = fetch(
            self.options.apiurl + self.options.geocoding_api_version + '/geocoding/' + encodeURI(country) + '/' + encodeURIComponent(zipcode) + '?formatVersion=2',
            {
                'method': 'GET',
            }
        );

        self.last_xhr = xhr;

        return xhr.then(response => response.json());
    }
}

export default Geolocation;
